import { useEffect, useRef, useState } from "react";
import {
  formatCountdown,
  formatRiel,
  formatUsd,
  type Cents,
  type Product,
} from "@moomoo/core";
import type { Slot } from "../lib/api.js";
import type { Translate } from "../lib/i18n.js";

/** Shared pieces. Small on purpose: each one does a single visible thing. */

export function Money({ value, riel = false }: { value: Cents; riel?: boolean }) {
  return (
    <span>
      <span className="pr">{formatUsd(value)}</span>
      {riel ? <span className="riel">{formatRiel(value)}</span> : null}
    </span>
  );
}

/* ── glyphs: one stroke weight, drawn inline so there is no icon font to load ── */

function Glyph({ children }: { children: React.ReactNode }) {
  return (
    <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
      {children}
    </svg>
  );
}

export function SearchIcon() {
  return (
    <Glyph>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </Glyph>
  );
}

export function PhoneRing() {
  return (
    <Glyph>
      <path d="M5 4h3.5l1.5 4-2 1.5a11 11 0 0 0 6.5 6.5L16 14l4 1.5V19a1 1 0 0 1-1 1A15 15 0 0 1 4 5a1 1 0 0 1 1-1z" />
    </Glyph>
  );
}

export function PinIcon() {
  return (
    <Glyph>
      <path d="M12 21s-6-5.3-6-10a6 6 0 0 1 12 0c0 4.7-6 10-6 10z" />
      <circle cx="12" cy="11" r="2.2" />
    </Glyph>
  );
}

export function ChevronLeft() {
  return (
    <Glyph>
      <path d="M14.5 5.5 8 12l6.5 6.5" />
    </Glyph>
  );
}

export function PhoneIcon() {
  return (
    <Glyph>
      <path d="M5 4h3.5l1.5 4-2 1.5a11 11 0 0 0 6.5 6.5L16 14l4 1.5V19a1 1 0 0 1-1 1A15 15 0 0 1 4 5a1 1 0 0 1 1-1z" />
    </Glyph>
  );
}

export function MapIcon() {
  return (
    <Glyph>
      <path d="M3 6.5 9 4l6 2.5L21 4v13.5L15 20l-6-2.5L3 20z" />
      <path d="M9 4v13.5M15 6.5V20" />
    </Glyph>
  );
}

export function CheckIcon() {
  return (
    <Glyph>
      <path d="M5 12.5 10 17.5 19 7" />
    </Glyph>
  );
}

export function CrossIcon() {
  return (
    <Glyph>
      <path d="M6 6l12 12M18 6 6 18" />
    </Glyph>
  );
}

export function LockIcon() {
  return (
    <Glyph>
      <rect x="5" y="10.5" width="14" height="10" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </Glyph>
  );
}

export function LocateIcon() {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="6.5" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3" />
    </Glyph>
  );
}

/**
 * The shop's masthead: brand on the sky tint, then where the milk is going.
 * `extra` is the small trailing action (the business switch) when there is one.
 */
export function Header({
  title,
  where,
  lang,
  onLang,
  extra,
}: {
  title: string;
  where: React.ReactNode;
  lang: "en" | "kh";
  onLang: () => void;
  extra?: React.ReactNode;
}) {
  return (
    <header className="top">
      <div className="brandrow">
        <img className="logo" src="/img/logo.png" alt="" width={42} height={42} />
        <h1>{title}</h1>
        <button type="button" className="lang" onClick={onLang}>
          {lang === "en" ? "ភាសាខ្មែរ" : "English"}
        </button>
      </div>
      <div className="where">
        <PinIcon />
        <span>{where}</span>
        {extra}
      </div>
    </header>
  );
}

/** A round back button and the screen's name. Every screen after the shop starts with one. */
export function ScreenBar({
  title,
  onBack,
  backLabel,
  aside,
}: {
  title: string;
  onBack?: () => void;
  backLabel: string;
  aside?: React.ReactNode;
}) {
  return (
    <div className="screenbar">
      {onBack ? (
        <button type="button" className="backbtn" onClick={onBack} aria-label={backLabel}>
          <ChevronLeft />
        </button>
      ) : null}
      <h2>{title}</h2>
      {aside ? <span>{aside}</span> : null}
    </div>
  );
}

/**
 * The cutoff clock. The server says how long is left; this ticks it down
 * locally so the number moves, and re-syncs whenever the server is asked again.
 */
export function Countdown({ slot, t }: { slot: Slot | undefined; t: Translate }) {
  const [ms, setMs] = useState(slot?.msToCutoff ?? 0);
  const base = useRef<{ ms: number; at: number }>({ ms: 0, at: Date.now() });

  useEffect(() => {
    if (!slot) return;
    base.current = { ms: slot.msToCutoff, at: Date.now() };
    setMs(slot.msToCutoff);
  }, [slot]);

  useEffect(() => {
    if (!slot) return;
    const timer = setInterval(() => {
      const elapsed = Date.now() - base.current.at;
      setMs(Math.max(0, base.current.ms - elapsed));
    }, 1000);
    return () => clearInterval(timer);
  }, [slot]);

  if (!slot) return null;
  // a ticking clock only earns its place in the last hour; the rest of the day it is just stress
  const hurry = slot.sameDay && ms > 0 && ms < 60 * 60_000;
  const remaining = Math.max(0, Math.min(1, ms / (60 * 60_000)));
  return (
    <div className={slot.sameDay ? "morning" : "morning shut"}>
      <div className="txt">
        <h2>{slot.sameDay ? t("deliveryToday") : t("deliveryTomorrow")}</h2>
        <p>{hurry ? t("hurry") : slot.sameDay ? t("cutoffOpen") : t("cutoffShut")}</p>
      </div>
      {hurry ? (
        <div className="time">
          {formatCountdown(ms)}
          <small>{t("left")}</small>
        </div>
      ) : null}
      {hurry ? (
        <div className="drain" aria-hidden="true">
          <i style={{ transform: `scaleX(${remaining.toFixed(4)})` }} />
        </div>
      ) : null}
    </div>
  );
}

/** Stock is silent unless it needs attention: a shelf full of "in stock" is noise. */
export function Stock({ product, t }: { product: Product; t: Translate }) {
  if (product.stock === 0) return <div className="stock gone">{t("soldOut")}</div>;
  if (product.stock < 250) return <div className="stock low">{t("onlyLeft", product.stock)}</div>;
  return null;
}

export function Counter({
  qty,
  onDown,
  onUp,
  label,
}: {
  qty: number;
  onDown: () => void;
  onUp: () => void;
  label: string;
}) {
  return (
    <span className="counter">
      <button type="button" onClick={onDown} aria-label={`One fewer ${label}`}>
        &minus;
      </button>
      <span className="q">{qty}</span>
      <button type="button" onClick={onUp} aria-label={`One more ${label}`}>
        +
      </button>
    </span>
  );
}

export function tileFor(code: string): string {
  // A2 1L plastic has no photograph of its own; the glass bottle is the closest truth.
  const alias = code === "A2-MILK-1L-PLASTIC" ? "A2-MILK-1L-GLASS" : code;
  return `/img/tile-${alias}.jpg`;
}
